import {useEffect} from 'react'
import i18next from 'i18next'
import {useAuthStore} from '@/features/auth/store/store'
import {
    IMPERSONATION_DURATION_MS,
    IMPERSONATION_ENDED_NOTICE,
    IMPERSONATION_WARNING_THRESHOLD_MS
} from '@/features/auth/constants.ts'
import {toast} from '@/utilities/toast/toast.tsx'

const EXPIRY_WARNING_TOAST_ID = 'impersonation-expiry-warning'

/**
 * Drives the impersonated session's fixed 1-hour lifetime: warns shortly before it ends, then hands the
 * operator back their own session at expiry — without waiting for a request to fail first.
 *
 * Both schedules are re-derived from the persisted start timestamp on every mount, so reloading mid-session
 * (including the reload that starts impersonation) rebuilds them instead of losing them. The axios 401
 * branch stays as the fallback for when the timer can't fire, e.g. a suspended tab.
 */
export const useImpersonationExpiryTimer = () => {
    const impersonating = useAuthStore(state => state.impersonating)
    const impersonationStartedAt = useAuthStore(state => state.impersonationStartedAt)

    // Delivers the notice left behind by whichever restore path triggered the reload that got us here.
    useEffect(() => {
        if (!sessionStorage.getItem(IMPERSONATION_ENDED_NOTICE)) return
        sessionStorage.removeItem(IMPERSONATION_ENDED_NOTICE)
        toast.info(i18next.t('impersonation:session_ended'))
    }, [])

    useEffect(() => {
        if (!impersonating || !impersonationStartedAt) return

        const expiresAt = impersonationStartedAt + IMPERSONATION_DURATION_MS
        const warnAt = expiresAt - IMPERSONATION_WARNING_THRESHOLD_MS

        const endSession = () => {
            toast.dismiss(EXPIRY_WARNING_TOAST_ID)
            if (useAuthStore.getState().restoreOriginalSession()) {
                sessionStorage.setItem(IMPERSONATION_ENDED_NOTICE, '1')
                window.location.reload()
            }
        }

        // A session already past its warning window (reload near expiry) warns immediately, hence max(0, …).
        const warningTimeout = setTimeout(
            () =>
                toast.info(
                    i18next.t('impersonation:expiry_warning', {
                        minutes: Math.ceil(IMPERSONATION_WARNING_THRESHOLD_MS / 60_000)
                    }),
                    {id: EXPIRY_WARNING_TOAST_ID, duration: Infinity}
                ),
            Math.max(0, warnAt - Date.now())
        )
        const expiryTimeout = setTimeout(endSession, Math.max(0, expiresAt - Date.now()))

        return () => {
            clearTimeout(warningTimeout)
            clearTimeout(expiryTimeout)
        }
    }, [impersonating, impersonationStartedAt])
}
